import { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { ErrorMessage } from '../components/ErrorMessage'
import { AuthLayout, AuthLoading } from '../components/AuthLayout'
import { kratosPublicApi } from '../api/client'
import { record } from '../api/flows'
import { useI18n } from '../i18n'

const errorMessages: Record<string, string> = {
  invalid_request: 'The request is missing a required parameter or is otherwise malformed.',
  invalid_client: 'Client authentication failed.',
  invalid_grant: 'The provided authorization grant is invalid, expired, or revoked.',
  unauthorized_client: 'The client is not authorized to request an authorization code.',
  unsupported_grant_type: 'The authorization grant type is not supported.',
  invalid_scope: 'The requested scope is invalid, unknown, or malformed.',
  access_denied: 'The resource owner or authorization server denied the request.',
  server_error: 'The server encountered an unexpected condition.',
  temporarily_unavailable: 'The server is currently unable to handle the request.',
  login_required: 'User authentication is required.',
  consent_required: 'User consent is required.',
  interaction_required: 'User interaction is required.',
  flow_expired: 'Your session has expired. Please try again.',
  session_already_available: 'You are already signed in.',
  session_refresh_required: 'Please sign in again to continue.',
  session_aal2_required: 'Additional authentication is required to continue.',
  security_csrf_violation: 'The security check failed. Please start again.',
}

export default function ErrorPage() {
  const { t } = useI18n()
  const [searchParams] = useSearchParams()
  const id = searchParams.get('id')
  const [kratosError, setKratosError] = useState<{ id: string; reason: string; localReason?: boolean } | null>(null)
  const error = kratosError?.id || searchParams.get('error') || 'unknown_error'
  const knownMessage = Object.prototype.hasOwnProperty.call(errorMessages, error) ? errorMessages[error] : undefined
  const rawDescription = kratosError?.reason || searchParams.get('error_description')
  let errorDescription = t('An unexpected error occurred.')
  if (knownMessage) errorDescription = t(knownMessage)
  else if (rawDescription) errorDescription = kratosError?.localReason ? t(rawDescription) : rawDescription
  const hint = searchParams.get('hint') || ''

  useEffect(() => {
    let active = true
    setKratosError(null)
    if (!id) return
    kratosPublicApi.getError(id).then(response => {
      if (!active) return
      const details = record(record(response.data).error)
      setKratosError({
        id: typeof details.id === 'string' ? details.id : 'authentication_error',
        reason: typeof details.reason === 'string' ? details.reason : 'Unable to complete authentication. Please try again.',
        localReason: typeof details.reason !== 'string',
      })
    }).catch(() => {
      if (active) setKratosError({ id: 'authentication_error', reason: 'The error details are no longer available. Please start again.', localReason: true })
    })
    return () => { active = false }
  }, [id])

  return (
    <AuthLayout title={t('Error')}>
      {id && !kratosError ? <AuthLoading message={t('Loading error details...')} /> : <>
        <ErrorMessage
          message={errorDescription}
          title={t('Error: {code}', { code: error })}
        />

        {hint && (
          <div className="mt-4 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-800 break-words">
              <span className="font-medium">{t('Hint:')}</span> {hint}
            </p>
          </div>
        )}

      </>}
      <div className="mt-6 flex flex-col gap-3">
        <Link to="/login" className="w-full btn-primary text-center">
          {t('Try Again')}
        </Link>
        <Link to="/" className="w-full btn-secondary text-center">
          {t('Go Home')}
        </Link>
      </div>
    </AuthLayout>
  )
}
